import type { ErrorContext } from "@productlane/lib"

import type { ZeroLogEntry } from "./zero-log-buffer"
import type { ZeroStorageProbe } from "./zero-storage-probe"
import { scrubZeroMessage } from "./zero-log-buffer"

export const ZERO_REPORT_COOLDOWN_MS = 10 * 60 * 1000
export const ZERO_REASON_MESSAGE_MAX_CHARS = 4000

// Constant and free of the token/status wording that isExpectedIntegrationError
// drops, so a real connect failure always reaches Sentry.
export const ZERO_FAILURE_MESSAGE = "Zero sync connection failed"

export interface ZeroConnectionSnapshot {
	stateName: string
	reasonKind: string | null
	reasonMessage: string | null
}

export interface ZeroReportGate {
	key: string | null
	at: number
}

export function readZeroConnectionSnapshot(
	state: unknown,
): ZeroConnectionSnapshot {
	const { name, reason } = (state ?? {}) as {
		name?: unknown
		reason?: unknown
	}
	const { kind, message } = (reason ?? {}) as {
		kind?: unknown
		message?: unknown
	}
	return {
		stateName: typeof name === "string" ? name : "unknown",
		reasonKind: typeof kind === "string" ? kind : null,
		reasonMessage: typeof message === "string" ? message : null,
	}
}

// `error` is fatal and never retries on its own. A `disconnected` Offline is the
// 60 second timeout, the only disconnect that means the client gave up.
export function zeroFailureKey(
	snapshot: ZeroConnectionSnapshot,
): string | null {
	if (snapshot.stateName === "error")
		return `error:${snapshot.reasonKind ?? "unknown"}`
	if (
		snapshot.stateName === "disconnected" &&
		snapshot.reasonKind === "Offline"
	)
		return "disconnected:Offline"
	return null
}

// An open tab from before a deploy asks for columns the new schema dropped; Zero
// reloads it onto the new code. Only that case is skipped: a table zero-cache
// cannot sync at all (the 2026-10-19 outage) carries the same error name.
export function isStaleSchemaReload(
	logs: readonly { message: string }[],
): boolean {
	// Zero also logs a bare "SchemaVersionNotSupported reloading" line with no
	// detail; only lines naming a table or column say what went wrong.
	const schemaErrors = logs.filter(
		(entry) =>
			entry.message.includes("SchemaVersionNotSupported") &&
			/The "[^"]+"/.test(entry.message),
	)
	return (
		schemaErrors.length > 0 &&
		schemaErrors.every((entry) =>
			entry.message.includes("is not one of the replicated columns"),
		)
	)
}

// Chrome's IndexedDB is broken on this machine (a corrupt or full profile), so
// Zero cannot open its local store; nothing on our side can fix that.
export function isBrowserStorageFailure(
	logs: readonly { message: string }[],
): boolean {
	return logs.some((entry) =>
		entry.message.includes(
			"Internal error opening backing store for indexedDB.open",
		),
	)
}

export function shouldReportZeroFailure(
	snapshot: ZeroConnectionSnapshot,
	gate: ZeroReportGate,
	now: number,
): boolean {
	const key = zeroFailureKey(snapshot)
	if (!key) return false
	if (gate.key === key && now - gate.at < ZERO_REPORT_COOLDOWN_MS) return false
	return true
}

export interface ZeroFailureReportInput {
	snapshot: ZeroConnectionSnapshot
	workspaceId?: string | null
	userId?: string | null
	clientID?: string | null
	clientGroupID?: string | null
	logs?: ZeroLogEntry[]
	storage?: ZeroStorageProbe | null
	navigatorOnline?: boolean | null
	visibility?: string | null
}

export function buildZeroFailureReport(input: ZeroFailureReportInput): {
	error: Error
	context: ErrorContext
} {
	const { snapshot } = input
	const kind = snapshot.reasonKind ?? "unknown"
	return {
		error: new Error(ZERO_FAILURE_MESSAGE),
		context: {
			domain: "zero",
			action: `connect (${snapshot.stateName}/${kind})`,
			workspaceId: input.workspaceId ?? null,
			userId: input.userId ?? null,
			extra: {
				stateName: snapshot.stateName,
				reasonKind: kind,
				reasonMessage: snapshot.reasonMessage
					? scrubZeroMessage(snapshot.reasonMessage).slice(
							0,
							ZERO_REASON_MESSAGE_MAX_CHARS,
						)
					: null,
				clientID: input.clientID ?? null,
				clientGroupID: input.clientGroupID ?? null,
				navigatorOnline: input.navigatorOnline ?? null,
				visibility: input.visibility ?? null,
				storage: input.storage ?? null,
				zeroLogs: input.logs ?? [],
			},
		},
	}
}
